/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import InformationButton from "../../../components/molecules/InformationButton"
import { getCountryFromLanguage } from "../../../core/service/BusinessLogicService"
import { isItalianTenant, loc } from "../../../core/utils"
import AutoCompleteSelect from "../../../form/AutoCompleteSelect"
import Date from "../../../form/Date"
import NumberField from "../../../form/NumberField"
import Select from "../../../form/Select"
import TextField from "../../../form/TextField"
import { DATE_FORMAT, contractTypeEnum } from "../constants/ClientPageConstants"
import { MARIED_VALUE } from "../constants/MaritalSituations"
import Bloc from "./Bloc"
import { ItalianIncomeNumberField } from "./IT"
import SmallBusinessFields from "./SmallBusinessFields"
import React from "react"
import { Col, FormGroup, Row } from "react-bootstrap"

export default function SituationBloc(props) {
  const { tunnelData, values, country, blocFields, completedFields } = props
  const { clientPageConfiguration, tenant } = tunnelData
  const { situation, identity, budget } = clientPageConfiguration
  const orderLanguageTag = tunnelData?.data?.order?.orderLanguageTag
  const countryCodeFromLanguage = getCountryFromLanguage(orderLanguageTag)
  const languageTag = orderLanguageTag || tunnelData?.lang || ""

  const appendLanguageTag = (url) => {
    if (!languageTag) return url
    return url + (url.includes("?") ? "&" : "?") + `languageTag=${encodeURIComponent(languageTag)}`
  }

  const prepareURLGetProfession = (tunnelData, values) => {
    const sectorQueryParam = values.situation?.workingSector?.label
      ? "?sector=" + values.situation?.workingSector?.label
      : ""
    const smallBusinessQueryParam = values?.identity?.isSmallBusiness
      ? sectorQueryParam
        ? "&isSmallBusiness=1"
        : "?isSmallBusiness=1"
      : ""
    const countryCode = getCountryFromLanguage(tunnelData?.data?.order?.orderLanguageTag)
    return appendLanguageTag(
      tunnelData.apiUrl +
        "middle-business-service/api/v2/repository/socio-professional-category/" +
        countryCode +
        sectorQueryParam +
        smallBusinessQueryParam
    )
  }

  function isClientMaried() {
    let isParcoursFR = tunnelData?.data?.order?.financerType === "PFLOC"
    return identity?.includes("maidenName") && isParcoursFR && values?.situation?.maritalStatus?.value === MARIED_VALUE
  }
  return (
    <Bloc hide={!situation} name="My situation" completedFields={completedFields} totalFieldsNumber={blocFields.length}>
      <div className="hard-shadow-white-block">
        <Row>
          {situation?.includes("workingSector") && (
            <Col lg={6}>
              <Select
                name="situation.workingSector"
                label={loc`working Sector`}
                placeholder={loc`Select...`}
                url={
                  tunnelData.apiUrl +
                  "middle-business-service/api/v2/repository/working-sectors/codes/" +
                  countryCodeFromLanguage
                }
                token={tunnelData.access_token}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.situation?.profession?.editable === false
                }
              />
            </Col>
          )}
          {situation?.includes("profession") && (
            <Col lg={6}>
              <Select
                name="situation.profession"
                label={loc`Occupation`}
                placeholder={loc`Select...`}
                url={prepareURLGetProfession(tunnelData, values)}
                token={tunnelData.access_token}
              />
            </Col>
          )}
          {values?.situation?.showProfessionelUse && situation?.includes("professionalUse") && (
            <Col lg={6}>
              <Select
                name="situation.professionalUse"
                label={loc`professional Use`}
                placeholder={loc`Select...`}
                url={
                  tunnelData.apiUrl +
                  "middle-business-service/api/v2/repository/professional-use/codes/" +
                  countryCodeFromLanguage
                }
                token={tunnelData.access_token}
              />
            </Col>
          )}
          {values?.situation?.showLineOfBusiness && situation?.includes("lineOfBusiness") && (
            <Col lg={6}>
              <Select
                name="situation.lineOfBusiness"
                label={loc`line Of Business`}
                placeholder={loc`Select...`}
                url={
                  tunnelData.apiUrl +
                  "middle-business-service/api/v2/repository/line-of-business/codes/" +
                  countryCodeFromLanguage +
                  "/" +
                  values.situation?.workingSector?.label
                }
                token={tunnelData.access_token}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.situation?.profession?.editable === false
                }
              />
            </Col>
          )}

          {values?.situation?.showContryActivityInfo && situation?.includes("countryOfActivity") && (
            <Col lg={6}>
              <Select
                name="situation.countryOfActivity"
                label={loc`country Of Activity`}
                placeholder={loc`Select...`}
                url={
                  tunnelData.apiUrl +
                  "middle-business-service/api/v2/repository/countries/codes/" +
                  countryCodeFromLanguage +
                  `?languageTag=${encodeURIComponent(languageTag)}`
                }
                token={tunnelData.access_token}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.situation?.profession?.editable === false
                }
              />
            </Col>
          )}
          {!values?.hideEmploymentInfo &&
            situation?.includes("employmentDate") &&
            situation?.includes("profession") &&
            !values?.situation?.hideEmploymentDate && (
              <Col lg={6}>
                <Date
                  name="situation.employmentDate"
                  label={country === "IT" ? loc`Employment start date` : loc`Since...`}
                  placeholder={country === "PT" && "MM/AAAA"}
                  format={DATE_FORMAT.situation.employmentDate[country]}
                  disabled={
                    tunnelData.data.order.simplifiedJourneyEnabled &&
                    tunnelData?.simplifiedJourneyInputConfiguration?.situation?.employmentDate?.editable === false
                  }
                />
              </Col>
            )}
          {!values?.situation?.hideNetMonthlyIncome && (
            <>
              {!!values?.identity?.isSmallBusiness ? (
                <SmallBusinessFields
                  tunnelData={tunnelData}
                  values={values}
                  clientPageConfiguration={clientPageConfiguration}
                  country={country}
                />
              ) : (
                <>
                  {situation?.includes("workingSituation") && (
                    <>
                      <Col lg={6}>
                        <Select
                          name="situation.workingSituation"
                          label={loc`Contract type`}
                          url={
                            tunnelData.apiUrl +
                            "middle-business-service/api/v2/repository/working-situation/" +
                            countryCodeFromLanguage
                          }
                          token={tunnelData.access_token}
                        />
                      </Col>
                      {values?.situation?.workingSituation?.value === contractTypeEnum &&
                      situation?.includes("endContractDate") ? (
                        <Col lg={6}>
                          <Date
                            name="situation.endContractDate"
                            label={loc`endContractDate`}
                            format={DATE_FORMAT.situation.endContractDate[country]}
                            disabled={
                              tunnelData.data.order.simplifiedJourneyEnabled &&
                              tunnelData?.simplifiedJourneyInputConfiguration?.situation?.endContractDate?.editable ===
                                false
                            }
                          />
                        </Col>
                      ) : null}
                    </>
                  )}
                  {situation?.includes("netMonthlyIncome") && (
                    <Col lg={6}>
                      {isItalianTenant(tenant) ? (
                        <ItalianIncomeNumberField
                          isDisabled={
                            tunnelData.data.order.simplifiedJourneyEnabled &&
                            tunnelData?.simplifiedJourneyInputConfiguration?.situation?.netMonthlyIncome?.editable ===
                              false
                          }
                        />
                      ) : (
                        <NumberField
                          name="situation.netMonthlyIncome"
                          label={loc`Your net monthly income`}
                          placeholder="€"
                          inputmode="numeric"
                          disabled={
                            tunnelData.data.order.simplifiedJourneyEnabled &&
                            tunnelData?.simplifiedJourneyInputConfiguration?.situation?.netMonthlyIncome?.editable ===
                              false
                          }
                        />
                      )}
                    </Col>
                  )}
                </>
              )}
            </>
          )}

          {values?.situation?.showCompanyName && situation?.includes("companyName") && (
            <Col lg={6}>
              <TextField
                name="situation.companyName"
                label={loc`Company name`}
                trimOnBlur={true}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.situation?.companyName?.editable === false
                }
              />
            </Col>
          )}
          {situation?.includes("companyCity") && !values?.situation?.hideCompanyInfo && (
            <Col lg={12}>
              <AutoCompleteSelect
                name="situation.companyCity"
                label={loc`Company city`}
                url={
                  tunnelData.apiUrl +
                  "middle-business-service/api/v2/repository/cities/" +
                  countryCodeFromLanguage +
                  `?city=INPUT_VALUE&limit=10&languageTag=${encodeURIComponent(languageTag)}`
                }
                token={tunnelData.access_token}
              />
            </Col>
          )}
          {situation?.includes("companyProvince") && !values?.situation?.hideCompanyInfo && (
            <Col lg={6}>
              <TextField name="situation.companyProvince" label={loc`Company province`} disabled={true} />
            </Col>
          )}
          {situation?.includes("yearBankSeniority") && (
            <Col lg={6}>
              <Date
                name="situation.yearBankSeniority"
                label={loc`Bank Seniority`}
                format={DATE_FORMAT.situation.residenceDate[country]}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.situation?.yearBankSeniority?.editable === false
                }
              />
            </Col>
          )}
          {situation?.includes("housingType") && (
            <Col lg={6}>
              <Select
                name="situation.housingType"
                label={loc`Housing`}
                url={appendLanguageTag(
                  tunnelData.apiUrl +
                    "middle-business-service/api/v2/repository/housing-types/" +
                    countryCodeFromLanguage
                )}
                token={tunnelData.access_token}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.situation?.housingType?.editable === false
                }
              />
            </Col>
          )}
          {situation?.includes("residenceDate") && situation?.includes("housingType") && (
            <Col lg={6}>
              <Date
                name="situation.residenceDate"
                label={loc`Since...`}
                placeholder={country === "PT" && "AAAA"}
                format={DATE_FORMAT.situation.residenceDate[country]}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.situation?.residenceDate?.editable === false
                }
              />
            </Col>
          )}
          {situation?.includes("maritalStatus") && (
            <Col lg={6} id="taubiraColOrder1">
              <Select
                name="situation.maritalStatus"
                label={loc`Family situation`}
                url={appendLanguageTag(
                  tunnelData.apiUrl +
                    "middle-business-service/api/v2/repository/marital-status/" +
                    countryCodeFromLanguage
                )}
                token={tunnelData.access_token}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.situation?.maritalStatus?.editable === false
                }
              />
            </Col>
          )}
          {situation?.includes("dependentChildrenNumber") && (
            <Col lg={6} id="taubiraColOrder3">
              <NumberField
                name="situation.dependentChildrenNumber"
                label={loc`Number of dependent children`}
                disabled={
                  tunnelData.data.order.simplifiedJourneyEnabled &&
                  tunnelData?.simplifiedJourneyInputConfiguration?.situation?.dependentChildrenNumber?.editable ===
                    false
                }
              />
            </Col>
          )}
          {isClientMaried() && (
            <Col lg={12} id="taubiraColOrder2">
              <FormGroup>
                <Row noGutters>
                  <Col>
                    <TextField
                      noFormGroup
                      name="situation.maidenName"
                      label={loc("Maiden name")}
                      trimOnBlur={true}
                      disabled={
                        tunnelData.data.order.simplifiedJourneyEnabled &&
                        tunnelData?.simplifiedJourneyInputConfiguration?.situation?.maidenName?.editable === false
                      }
                    >
                      <InformationButton overlayText={loc("If you are not married, enter your name again.")} />
                    </TextField>
                  </Col>
                </Row>
              </FormGroup>
            </Col>
          )}
        </Row>
      </div>
    </Bloc>
  )
}
